import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const categories = [
  { name: 'Memory Foam', path: '/products/memory-foam' },
  { name: 'Down & Feather', path: '/products/down-feather' },
  { name: 'Cooling Pillows', path: '/products/cooling' },
  { name: 'Orthopedic', path: '/products/orthopedic' },
  { name: 'Travel Pillows', path: '/products/travel' },
  { name: 'Body Pillows', path: '/products/body' },
];

const navItems = [
  { name: 'Shop All', path: '/products' },
  { name: 'Best Sellers', path: '/products?filter=bestseller' },
  { name: 'Sale', path: '/products?filter=sale' },
  { name: 'About', path: '/about' },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoryMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCategoryMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCategorySelect = (path) => {
    navigate(path);
    handleCategoryMenuClose();
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography
        variant="h6"
        sx={{
          my: 2,
          fontFamily: '"Playfair Display", serif',
          fontWeight: 600,
          color: 'primary.main',
        }}
      >
        Sleepout
      </Typography>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/products">
            <ListItemText primary="Shop All Pillows" />
          </ListItemButton>
        </ListItem>
        {categories.map((category) => (
          <ListItem key={category.name} disablePadding>
            <ListItemButton component={RouterLink} to={category.path} sx={{ pl: 4 }}>
              <ListItemText primary={category.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        {navItems.slice(1).map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton component={RouterLink} to={item.path}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Top announcement bar */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
          Free Shipping on Orders Over ₹1,999 • 100-Night Sleep Trial
        </Typography>
      </Box>

      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: '1px solid #eee' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
            {/* Mobile menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Typography
              variant="h5"
              component={RouterLink}
              to="/"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 600,
                color: 'primary.main',
                textDecoration: 'none',
                flexGrow: { xs: 1, md: 0 },
                mr: { md: 4 },
              }}
            >
              Sleepout
            </Typography>

            {/* Desktop navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              <Button
                onClick={handleCategoryMenuOpen}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ color: 'text.primary', fontWeight: 500 }}
              >
                Pillow Types
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCategoryMenuClose}
                MenuListProps={{ sx: { minWidth: 200 } }}
              >
                {categories.map((category) => (
                  <MenuItem
                    key={category.name}
                    onClick={() => handleCategorySelect(category.path)}
                  >
                    {category.name}
                  </MenuItem>
                ))}
              </Menu>
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: item.name === 'Sale' ? 'error.main' : 'text.primary',
                    fontWeight: item.name === 'Sale' ? 600 : 500,
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Box>

            {/* Right icons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="inherit" aria-label="search">
                <SearchIcon />
              </IconButton>
              <IconButton color="inherit" aria-label="account">
                <PersonOutlineIcon />
              </IconButton>
              <IconButton color="inherit" aria-label="cart">
                <ShoppingCartOutlinedIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Header;
